<!-- Profile → Settings' SUBSCRIPTIONS (2026-10-20, Seán's spec; titled so 2026-10-23): a switch
     per mailing list, saved as publicMetadata.subscriptions (subscriptionLists.js), any
     combination, none included. An
     assistant also has the copies switch here, first (it's email too; ROADMAP.md § Registering
     on behalf of others, stage 6). -->
<template>
  <AppModalProfileSettingsSwitches
    title="Subscriptions"
    description="Get updates about:"
    :rows
    :is-saving="isSaving"
    @toggle="handleToggle">
    <!-- what the switches mean, by how many are on (Seán, 2026-10-23) -->
    <template #help>
      <AppHelpPopup
        id="subscribeHelp"
        class="subscribeHelp"
        label="About your email updates"
        is-in-dialog
        bounds-selector=".content">
        <template v-if="lists.length === SUBSCRIPTION_LISTS.length">
          <span class="helpParagraph">
            We'll send you email updates about all upcoming VIZBI events.
          </span>
          <span class="helpParagraph">
            We may also send you emails about events you've registered for.
          </span>
        </template>
        <template v-else-if="lists.length">
          <span class="helpParagraph">
            We'll send you email updates about upcoming VIZBI {{ chosenNames }}.
          </span>
          <span class="helpParagraph">
            We may also send you emails about events you've registered for.
          </span>
        </template>
        <template v-else>
          <span class="helpParagraph">
            We won't send you email updates about VIZBI events you haven't registered for.
          </span>
          <span class="helpParagraph">
            We may still send you emails about events you've registered for.
          </span>
        </template>
      </AppHelpPopup>
    </template>
  </AppModalProfileSettingsSwitches>
</template>

<script setup>
import {computed, ref, watch, watchEffect} from "vue";
import AppHelpPopup from "@/components/App/HelpPopup.vue";
import AppModalProfileSettingsSwitches from "@/components/App/Modal/Profile/SettingsSwitches";
import {isSubscribeRequested, useSaveIsSubscribed} from "@/composables/useSaveIsSubscribed";
import {SUBSCRIPTION_LISTS, getSubscribedTo} from "@/composables/subscriptionLists";
import useUserSafe from "@/composables/useUserSafe";

const LABELS = {workshop: "Workshops", masterclass: "Masterclasses", showcase: "Showcases"};
// in the help's sentence: "workshops and masterclasses"
const NAMES = {workshop: "workshops", masterclass: "masterclasses", showcase: "showcases"};
const COPIES_KEY = "isCopiedOnParticipantEmails";
// under "Get updates about:" (Seán, 2026-10-20; was "Send Me Copies of Emails to the …")
const COPIES_LABEL = "Participants I manage"; // sentence case (Seán, 2026-10-20)
const SWITCH_DELAY_MS = 600; // the dialog is open first, so the switches are seen turning on

const {user} = useUserSafe();
const {isSaving, save} = useSaveIsSubscribed();
const metadata = computed(() => user.value?.publicMetadata || {});
const lists = ref([]);
const isCopied = ref(true);
watchEffect(() => {
  if (isSaving.value) return;
  lists.value = getSubscribedTo(metadata.value);
  isCopied.value = metadata.value[COPIES_KEY] !== false;
});

const chosenNames = computed(() => lists.value.map((list) => NAMES[list]).join(" and "));

// an assistant (marked by the server when someone names them, or they pay for others)
const isAssistant = computed(() => {
  return Boolean(metadata.value.isAssistant || metadata.value.purchases?.length);
});
const rows = computed(() => {
  const listRows = SUBSCRIPTION_LISTS.map((list) => {
    return {key: list, label: LABELS[list], value: lists.value.includes(list)};
  });
  const copiesRow = {key: COPIES_KEY, label: COPIES_LABEL, value: isCopied.value};
  return isAssistant.value ? [copiesRow, ...listRows] : listRows; // theirs first (Seán, 2026-10-20)
});

const getListsWith = (list, isOn) => {
  const chosen = isOn ? [...lists.value, list] : lists.value.filter((item) => item !== list);
  return SUBSCRIPTION_LISTS.filter((item) => chosen.includes(item));
};

// on at once; back if the save fails
async function saveLists(next) {
  const before = lists.value;
  lists.value = next;
  if (!(await save(user.value, {subscriptions: next}))) lists.value = before;
}

async function handleToggle(key, isOn) {
  if (isSaving.value) return;
  if (key !== COPIES_KEY) return await saveLists(getListsWith(key, isOn));
  isCopied.value = isOn;
  if (!(await save(user.value, {[COPIES_KEY]: isOn}))) isCopied.value = !isOn;
}

// a link asked to subscribe (requestSubscribe): every list on once signed in; they can turn
// any off
watch([isSubscribeRequested, user], ([isRequested, currentUser]) => {
  if (!isRequested || !currentUser) return;
  isSubscribeRequested.value = false;
  if (lists.value.length === SUBSCRIPTION_LISTS.length) return;
  setTimeout(() => saveLists([...SUBSCRIPTION_LISTS]), SWITCH_DELAY_MS);
}, {immediate: true});
</script>

<style scoped>
/* as PRIVACY's "?" (SettingsPrivacy.vue): a filled grey circle with a white mark */
.subscribeHelp {
  --help-color: #8a8a8a;
  font-size: 15px;
}
.subscribeHelp :deep(button.helpButton) {
  border-color: var(--help-color);
  background: var(--help-color);
  color: #fff;
}
</style>
